import { KalturaCategoryTree, KalturaMediaAsset } from '@kaltura-ott/ott-client-types/api/types';
import { KalturaImage } from '@kaltura-ott/ott-client-types/api/types/KalturaImage';

import {
  KuxCategoriesDTO,
  KuxFilteredCategoriesDTO,
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
} from '../interfaces';

import { getConfig, KuXConfig } from './config';
import { META_KSQL } from './utils/constants';
import {
  KalturaCategoryMetas,
  normalizeCategoryMetas,
  KuxCategoryMetas,
  normalizeCategoryTags as normalizeMultiTags,
} from './utils/meta';
import { KalturaCategoryTags, normalizeCategoryTags } from './utils/tags';

/**
 * Recursion to collect all UIDA-assetId in categoryTree
 *
 */
export function categoryItemsIdList(categoriesTree: KalturaCategoryTree[], onlyActive = true): number[] {
  let uidaAssetIdList: number[] = [];
  categoriesTree.forEach((categoryItem) => {
    // This check seems redundant. For non-admin tokens, categories are always returned with isActive === true.
    // TODO: Confirm this fact with the backend team. If true, remove the "onlyActive" flag from the code.
    if (onlyActive && !categoryItem.isActive) {
      return;
    }
    if (categoryItem.id) {
      uidaAssetIdList.push(Number(categoryItem.id));
    }
    if (categoryItem.children.length) {
      uidaAssetIdList = [...uidaAssetIdList, ...categoryItemsIdList(categoryItem.children, onlyActive)];
    }
  });

  return uidaAssetIdList;
}

/**
 * Recursion to set UIDA-asset meta and tags to origin categoryTree
 *
 * @param categoriesTree: KalturaCategoryTree[]
 * @param uidaAssets: KalturaAsset[]
 */
export function setUidaToCategoryTree(
  categoriesTree: KalturaCategoryTree[],
  uidaAssets: KalturaMediaAsset[],
): (KuxCategoriesDTO | undefined)[] {
  return categoriesTree
    .map((categoryItem) => {
      const kuxCategoryItem: KuxCategoriesDTO = {
        name: categoryItem.name,
        multilingualName: categoryItem.multilingualName,
        id: categoryItem.id,
        isActive: categoryItem.isActive,
        images: (categoryItem.images as unknown as KalturaImage[]) || [],
        metas: {},
        channels: categoryItem.unifiedChannels.map((channel) => channel.id),
        unifiedChannels: categoryItem.unifiedChannels.map((channel) => ({
          id: channel.id,
          name: channel.name,
        })),
        tags: {},
        originTags: {},
        children: [],
        structId: undefined,
        virtualAssetId: categoryItem.virtualAssetId,
        referenceId: categoryItem.referenceId || categoryItem.id.toString(),
      };

      const asset = uidaAssets.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (asset) => Number((asset.metas.CategoryId as any).value as number) === categoryItem.id,
      );

      if (asset) {
        if (asset.images && asset.images.length > 0) {
          kuxCategoryItem.images = [...asset.images] as unknown as KalturaImage[];
        }
        kuxCategoryItem.metas = normalizeCategoryMetas(asset.metas as KalturaCategoryMetas);
        kuxCategoryItem.tags = normalizeCategoryTags(asset.tags as KalturaCategoryTags);
        kuxCategoryItem.originTags = normalizeMultiTags(asset.tags);
        kuxCategoryItem.structId = asset.type;
      }

      const deviceBrandDenial = kuxCategoryItem.tags['device_brand_denial_list_for_ui_components'] ?? [];
      if (deviceBrandDenial.includes(getConfig(KuXConfig)('brandId')?.toString())) {
        return;
      }
      if (categoryItem.children.length) {
        kuxCategoryItem.children = setUidaToCategoryTree(categoryItem.children, uidaAssets);
      }
      return kuxCategoryItem;
    })
    .filter(Boolean);
}

export function mergeObjectValues(list: KuxFilteredCategoriesDTO['uiDesignElements'][]) {
  const objects = list;

  objects.forEach((obj) => {
    Object.keys(obj).forEach((key) => {
      // @ts-expect-error TODO Fix
      if (obj[key] === undefined || obj[key] === null || obj[key] === '' || obj[key] === 0) {
        // @ts-expect-error TODO Fix
        delete obj[key];
      }
    });
  });
  return Object.assign({}, ...objects);
}

/**
 * Calculate ui elements
 * @param metas
 * @param parentUiDesignElements
 */
export function calculateUiDesignElements(
  metas: KuxCategoryMetas,
  parentUiDesignElements?: KuxFilteredCategoriesDTO['uiDesignElements'],
): KuxFilteredCategoriesDTO['uiDesignElements'] {
  const uiElements = {
    btnFontSize: metas['btn_font_size'] as number,
    btnTextColor: metas['btn_text_color'] as string,
    btnTextColorFocus: metas['btn_text_color_focus'] as string,
    btnBackgroundColor: metas['btn_background_color'] as string,
    btnBackgroundColorFocus: metas['btn_background_color_focus'] as string,
    btnBackgroundOpacity: metas['btn_background_opacity'] as number,
    backgroundColor: metas['background_color'] as string,
    textColor: metas['text_color'] as string,
    fontType: metas['font_type'] as string,
    fontSize: metas['font_size'] as number,
    focusColor: metas['focus_color'] as string,
    selectedColor: metas['selected_color'] as string,
  };

  if (parentUiDesignElements) {
    return mergeObjectValues([parentUiDesignElements, uiElements]);
  }

  return uiElements;
}

/**
 * Separate calculate ui elements for categoryItem type 'menu' and for other types
 * @param categoryItem
 * @param mainMenuUiDesignElementsProps
 * @param parentUiDesignElements
 */
export function prepareCurrentUiDesignElements(
  categoryItem: KuxCategoriesDTO,
  mainMenuUiDesignElementsProps: KuxFilteredCategoriesDTO['uiDesignElements'],
  parentUiDesignElements?: KuxFilteredCategoriesDTO['uiDesignElements'],
): KuxFilteredCategoriesDTO['uiDesignElements'] {
  const { metas, tags } = categoryItem;

  const isCategoryItemMenu =
    categoryItem.tags.UIDA_structure_type === UIDAStructureTypeEnum.menu &&
    tags.UIDA_presentation_type === UIDAPresentationTypeEnum.menu;

  const currentUiDesignElements = isCategoryItemMenu
    ? calculateUiDesignElements(metas, mainMenuUiDesignElementsProps)
    : calculateUiDesignElements(metas, parentUiDesignElements);

  return currentUiDesignElements;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function replaceTemplate(string: string, templates: any) {
  const pattern = /%\s*(\w+?)\s*%/g; // %property%
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return string.replace(pattern, (_: string, token: any) => templates[token]() || '');
}

/**
 * Calculate aggregated KSQL
 */
export function calculateAggregatedKSQL(
  metas: KuxCategoryMetas,
  parentAggregatedKSQL?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templates?: any,
): string {
  let aggregatedKSQL = META_KSQL.map((item) => metas[item])
    .filter((item) => !!item)
    .map((item) => {
      // replace multiple spaces into one
      return (item as string).replace(/\s\s+/g, ' ');
    })
    .join(' ');

  if (metas['hide_adult_assets'] && metas['hide_adult_assets_kSQL']) {
    aggregatedKSQL = aggregatedKSQL
      ? `${aggregatedKSQL} ${metas['hide_adult_assets_kSQL']}`
      : `${metas['hide_adult_assets_kSQL']}`;
  }

  if (metas['hide_parental_assets'] && metas['hide_parental_assets_kSQL']) {
    aggregatedKSQL = aggregatedKSQL
      ? `${aggregatedKSQL} ${metas['hide_parental_assets_kSQL']}`
      : `${metas['hide_parental_assets_kSQL']}`;
  }

  if (metas['enable_filter_by_entitlement']) {
    aggregatedKSQL = aggregatedKSQL
      ? `entitled_assets='entitledSubscriptions' ${aggregatedKSQL}`
      : `entitled_assets='entitledSubscriptions'`;
  }

  if (parentAggregatedKSQL) {
    if (parentAggregatedKSQL.includes('(and ')) {
      parentAggregatedKSQL = parentAggregatedKSQL.replace('(and ', '');
      parentAggregatedKSQL = parentAggregatedKSQL.replace(/\)([^)]*)$/, '' + '$1');
    }

    aggregatedKSQL = aggregatedKSQL ? parentAggregatedKSQL + ' ' + aggregatedKSQL : parentAggregatedKSQL;
  }

  aggregatedKSQL = aggregatedKSQL ? `(and ${aggregatedKSQL})` : '';
  aggregatedKSQL = aggregatedKSQL.replace('and and ', 'and ');
  if (templates) {
    aggregatedKSQL = replaceTemplate(aggregatedKSQL, templates);
  }
  return aggregatedKSQL;
}

export function addKsql(ksql = '', newKsql: string): string {
  if (!ksql) {
    return newKsql;
  }

  const mergedKsql = `${ksql.substring(0, ksql.length - 1)} ${newKsql})`;

  return mergedKsql;
}
